<template>
  <Dialog :open="open" @update:open="handleClose" title="Add Base Model" size="md">
    <div class="space-y-4">
      <!-- Model Information -->
      <Card>
        <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Model Details</h4>
        <div class="space-y-2">
          <div class="flex justify-between">
            <span class="text-xs text-gray-500 dark:text-gray-400">Name:</span>
            <span class="text-sm font-medium text-gray-900 dark:text-white">{{ modelName }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-xs text-gray-500 dark:text-gray-400">Slug:</span>
            <code
              class="text-xs font-mono text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded"
            >
              {{ modelSlug }}
            </code>
          </div>
          <div class="flex justify-between">
            <span class="text-xs text-gray-500 dark:text-gray-400">Provider:</span>
            <span
              :class="[
                'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                providerBadgeColor,
              ]"
            >
              {{ providerDisplayName }}
            </span>
          </div>
        </div>
      </Card>

      <!-- Custom Name Input -->
      <div>
        <label
          for="custom-name"
          class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          Custom Name (Optional)
        </label>
        <Input
          id="custom-name"
          v-model="customName"
          placeholder="Leave empty to use slug as name"
          :disabled="isSubmitting"
          class="w-full"
        />
        <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
          If provided, you'll use this custom name in API requests instead of the slug.
        </p>
      </div>

      <!-- Error Display -->
      <Alert v-if="error" variant="error" class="mt-4">
        {{ error }}
      </Alert>

      <!-- Info Message -->
      <div
        class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3"
      >
        <div class="flex">
          <div class="flex-shrink-0">
            <svg
              class="h-5 w-5 text-blue-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-blue-800 dark:text-blue-300">About Custom Names</h3>
            <div class="mt-1 text-sm text-blue-700 dark:text-blue-400">
              <p>
                Custom names allow you to reference models with friendly names in your API requests.
                If not provided, the model slug will be used as the name.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex justify-end space-x-3 pt-4">
        <Button type="button" variant="outline" @click="handleClose" :disabled="isSubmitting">
          Cancel
        </Button>
        <Button type="button" variant="primary" @click="handleSubmit" :loading="isSubmitting">
          Add to Organization
        </Button>
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue';
  import { Dialog, Button, Input, Alert, Card } from '@asabdev/asab-ui';
  import type { Provider } from '../types';

  interface Props {
    open: boolean;
    modelSlug: string;
    modelName: string;
    provider: Provider;
  }

  interface Emits {
    (e: 'update:open', value: boolean): void;
    (e: 'submit', data: { slug: string; name?: string }): void;
    (e: 'cancel'): void;
  }

  const props = defineProps<Props>();
  const emit = defineEmits<Emits>();

  // Local state
  const customName = ref('');
  const isSubmitting = ref(false);
  const error = ref<string | null>(null);

  // Computed properties
  const providerDisplayName = computed(() => {
    const providerMap: Record<Provider, string> = {
      openai: 'OpenAI',
      anthropic: 'Anthropic',
      google: 'Google',
      groq: 'Groq',
    };
    return providerMap[props.provider] || props.provider;
  });

  const providerBadgeColor = computed(() => {
    const colorMap: Record<Provider, string> = {
      openai: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
      anthropic: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
      google: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
      groq: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
    };
    return (
      colorMap[props.provider] || 'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-300'
    );
  });

  // Methods
  const handleSubmit = () => {
    // Clear any previous errors
    error.value = null;

    // Validate custom name if provided
    const trimmedName = customName.value.trim();
    if (trimmedName && trimmedName.length > 255) {
      error.value = 'Custom name must be 255 characters or less';
      return;
    }

    // Set submitting state
    isSubmitting.value = true;

    // Emit submit event with slug and optional custom name
    emit('submit', {
      slug: props.modelSlug,
      name: trimmedName || undefined,
    });

    // Reset submitting state after a short delay
    // The parent component should handle closing the modal
    setTimeout(() => {
      isSubmitting.value = false;
    }, 500);
  };

  const handleClose = () => {
    if (isSubmitting.value) return; // Prevent closing while submitting

    // Reset state
    customName.value = '';
    error.value = null;
    isSubmitting.value = false;

    emit('update:open', false);
    emit('cancel');
  };

  // Watch for modal open/close to reset state
  watch(
    () => props.open,
    newValue => {
      if (!newValue) {
        // Reset state when modal closes
        customName.value = '';
        error.value = null;
        isSubmitting.value = false;
      }
    }
  );
</script>
